import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    OnInit,
    ViewChild,
} from '@angular/core';
import { CmpsDialogRef } from '@cmps/ng-uikit';
import { CmpsCommentComponent } from 'app/evaluation/comment';
import { tap } from 'rxjs/operators';

import {
    takeUntilDestroy,
    TakeUntilDestroy,
} from '../../../common/take-until-destroy.decorator';
import { useSelector2 } from '../../store-use-selector';
import { CaGuide, CaMeetingParamsContext } from '../core';
import { CaAnalysisDialogService } from '../dialog';
import { CaAnalysisDialogState } from '../dialog/ca-analysis-dialog.state';

import { TranslationKeyMap } from './ca-guide-picker-dialog.component.i18n';

@Component({
    selector: 'cmps-ca-guide-picker-dialog',
    templateUrl: './ca-guide-picker-dialog.component.html',
    host: {
        class: 'cmps-ca-guide-picker-dialog',
    },
})
@TakeUntilDestroy()
export class CaGuidePickerDialogComponent implements OnInit {
    /** @private */
    _i18n = TranslationKeyMap;

    /** @private */
    _pickedGuide: CaGuide;

    /** @private */
    _view: 'recommended' | 'library' = 'recommended';

    /** @private */
    _selectedLibraryGuide: CaGuide | null = null;

    /** @private */
    _dispositionComment: string | null = null;

    /** @private */
    _dispositionShown = false;

    @ViewChild('commentRef')
    private readonly _commentRef: CmpsCommentComponent;

    constructor(
        private readonly _cdr: ChangeDetectorRef,
        private readonly _elementRef: ElementRef<HTMLElement>,
        /** @private */ readonly _dialogRef: CmpsDialogRef<
            CaGuidePickerDialogComponent,
            CaMeetingParamsContext
        >,
        /** @private */ readonly _caService: CaAnalysisDialogService,
    ) {}

    ngOnInit(): void {
        useSelector2<CaGuide[] | null>(
            (state) =>
                (state?.coaching?.caAnalysis as CaAnalysisDialogState)
                    ?.meetingRequestContext?.guides,
        )
            .pipe(
                tap(() => {
                    this._setupGuides();
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    ngOnDestroy(): void {}

    /** @private */
    _switchToLibrary(): void {
        this._view = 'library';
        this._selectedLibraryGuide = null;
        this._dispositionComment = null;
        this._cdr.detectChanges();
        setTimeout(() => {
            const firstItem = this._elementRef.nativeElement.querySelector(
                '.cmps-ca-guide-picker-dialog--guide-item',
            ) as HTMLElement;
            firstItem?.focus();
        });
    }

    /** @private */
    _switchToRecommended(): void {
        this._view = 'recommended';
        this._selectedLibraryGuide = null;
        this._dispositionComment = null;
        this._cdr.detectChanges();
        setTimeout(() => {
            const title = this._elementRef.nativeElement.querySelector(
                '.cmps-ca-guide-picker-dialog--title',
            ) as HTMLElement;
            title?.focus();
        });
    }

    /** @private */
    get _sortedGuides(): CaGuide[] {
        const guides =
            this._caService.state?.meetingRequestContext?.guides ?? [];
        return [...guides].sort((a, b) => {
            if (a.recommended === true && b.recommended !== true) return -1;
            if (b.recommended === true && a.recommended !== true) return 1;
            return 0;
        });
    }

    /** @private */
    _onLibraryGuideSelect(guideConfigId: string): void {
        this._selectedLibraryGuide =
            this._caService.state?.meetingRequestContext?.guides?.find(
                (e) => e.guideConfigId === guideConfigId,
            ) ?? null;
        this._cdr.detectChanges();

        if (
            this._selectedLibraryGuide != null &&
            this._selectedLibraryGuide.recommended !== true
        ) {
            const firstShow = !this._dispositionShown;
            if (firstShow) {
                this._dispositionShown = true;
                this._cdr.detectChanges();
            }
            setTimeout(
                () => {
                    this._scrollToDisposition();
                    try {
                        this._commentRef?.focus();
                    } catch (_) {
                        // cmps-comment editor may not be ready
                    }
                },
                firstShow ? 500 : 0,
            );
        }
    }

    /** @private */
    _onDispositionChange(value: string): void {
        this._dispositionComment = value;
        this._cdr.detectChanges();
    }

    /** @private */
    _onConfirmLibraryClick(): void {
        this._dialogRef.close({
            guideConfigId: this._selectedLibraryGuide?.guideConfigId,
            guideConfigComment:
                this._selectedLibraryGuide?.recommended === true
                    ? null
                    : this._dispositionComment,
        });
    }

    /** @private */
    get _confirmLibraryBtnDisabled(): boolean {
        if (this._selectedLibraryGuide == null) return true;
        if (this._selectedLibraryGuide.recommended === true) return false;
        if (this._dispositionComment == null) return true;
        const el = document.createElement('div');
        el.innerHTML = this._dispositionComment;
        return el.textContent.trim().length === 0;
    }

    /** @private */
    _onContinueClick(): void {
        this._dialogRef.close({
            guideConfigId: this._pickedGuide?.guideConfigId,
        });
    }

    private _setupGuides(): void {
        this._pickedGuide =
            this._caService.state?.meetingRequestContext?.guides?.find(
                (e) => e.recommended === true,
            );
    }

    private _scrollToDisposition(): void {
        const scrollParent =
            this._elementRef.nativeElement.closest('.cmps-dialog-body');
        if (scrollParent != null) {
            scrollParent.scrollTop = scrollParent.scrollHeight;
        }
    }
}
